/**
 * Wishlist (favorites)
 *
 * State: array of product handles stored via theme.storageUtil at key 'wishlist'.
 * Custom elements:
 *   <wishlist-button data-product-handle="..."> — heart toggle on cards and PDP.
 *   <wishlist-count>                             — header icon; active when list is non-empty.
 * Sync: all instances listen for 'on:wishlist:change' dispatched on document.
 */

class Wishlist {
  static STORAGE_KEY = 'wishlist';
  static CHANGE_EVENT = 'on:wishlist:change';

  static getAll() {
    return theme.storageUtil.get(Wishlist.STORAGE_KEY, true) || [];
  }

  static has(handle) {
    return !!handle && Wishlist.getAll().includes(handle);
  }

  static get count() {
    return Wishlist.getAll().length;
  }

  static toggle(handle) {
    if (!handle) return false;
    const all = Wishlist.getAll();
    const idx = all.indexOf(handle);
    if (idx >= 0) {
      all.splice(idx, 1);
    } else {
      all.push(handle);
    }
    theme.storageUtil.set(Wishlist.STORAGE_KEY, all);
    document.dispatchEvent(new CustomEvent(Wishlist.CHANGE_EVENT));
    return idx < 0;
  }
}

if (!customElements.get('wishlist-button')) {
  class WishlistButton extends HTMLElement {
    constructor() {
      super();
      this.btn = this.querySelector('[js-btn]');
      this.init();
    }

    init() {
      this.bindEvents();
    }

    connectedCallback() {
      this.syncState();
      document.addEventListener(Wishlist.CHANGE_EVENT, this.syncState);
    }

    disconnectedCallback() {
      document.removeEventListener(Wishlist.CHANGE_EVENT, this.syncState);
    }

    bindEvents() {
      if (this.btn) {
        this.btn.addEventListener('click', (e) => this.onClick(e));
      }
    }

    get handle() {
      return this.dataset.productHandle;
    }

    onClick(e) {
      e.preventDefault();
      e.stopPropagation();
      Wishlist.toggle(this.handle);
    }

    syncState = () => {
      const active = Wishlist.has(this.handle);
      this.classList.toggle('wishlist-button--active', active);
      if (this.btn) {
        this.btn.setAttribute('aria-pressed', String(active));
        const labelKey = active ? 'labelRemove' : 'labelAdd';
        const fallback = active ? 'Remove from favorites' : 'Add to favorites';
        this.btn.setAttribute('aria-label', this.dataset[labelKey] || fallback);
      }
    };
  }

  customElements.define('wishlist-button', WishlistButton);
}

if (!customElements.get('wishlist-count')) {
  class WishlistCount extends HTMLElement {
    connectedCallback() {
      this.sync();
      document.addEventListener(Wishlist.CHANGE_EVENT, this.sync);
      window.addEventListener('pageshow', this.onPageShow);
    }

    disconnectedCallback() {
      document.removeEventListener(Wishlist.CHANGE_EVENT, this.sync);
      window.removeEventListener('pageshow', this.onPageShow);
    }

    onPageShow = (e) => {
      if (e.persisted) this.sync();
    };

    sync = () => {
      this.classList.toggle('wishlist-count--active', Wishlist.count > 0);
    };
  }

  customElements.define('wishlist-count', WishlistCount);
}
